import React, { useState, useEffect } from 'react';
import { Play, Trophy, User, ChevronDown, ChevronUp, QrCode } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { MANUAL_MATCH_PREFIX, MANUAL_SETUP_STORAGE_KEY } from '../../constants';
import { useAppConfig } from '../../hooks/useAppConfig';
import { SetupData } from '../../hooks/useMatchData';
import * as matchStorage from '../../storage/matchStorage';

interface FormData {
  playerA: string;
  playerB: string;
  scoringType: 'BestOf3' | 'BestOf5';
  competitionName: string;
  categoryName: string;
  refereeName: string;
  drawName: string;
  courtName: string;
  roundType: string;
  matchId: string;
  dateTime: string;
}

interface FormErrors {
  playerA?: string;
  playerB?: string;
  scoringType?: string;
}

interface MatchSetupScreenProps {
  setupData?: SetupData | null;
}

export function MatchSetupScreen({ setupData }: MatchSetupScreenProps) {
  const { t } = useTranslation();
  const { config, isLoading: isConfigLoading } = useAppConfig();

  const [formData, setFormData] = useState<FormData>({
    playerA: '',
    playerB: '',
    scoringType: (setupData?.scoringType as 'BestOf3' | 'BestOf5') || 'BestOf5',
    competitionName: '',
    categoryName: '',
    refereeName: '',
    drawName: '',
    courtName: setupData?.courtName || '',
    roundType: '',
    matchId: setupData?.matchId || '',
    dateTime: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  // Charger les préférences sauvegardées (nom de l'arbitre)
  useEffect(() => {
    try {
      const savedPrefs = localStorage.getItem(MANUAL_SETUP_STORAGE_KEY);
      if (savedPrefs) {
        const prefs = JSON.parse(savedPrefs);
        if (prefs.refereeName) {
          setFormData(prev => ({ ...prev, refereeName: prefs.refereeName }));
        }
      }
    } catch (e) {
      // Ignorer les erreurs de parsing
    }
  }, []);

  // Auto-generate matchId and dateTime (skip matchId when setupData provides it)
  useEffect(() => {
    const updateAutoFields = () => {
      const now = new Date();
      const dateTime = now.toISOString().slice(0, 16).replace('T', ' ');

      if (setupData) {
        setFormData(prev => ({ ...prev, dateTime }));
      } else {
        const matchId = MANUAL_MATCH_PREFIX + now.toISOString();
        setFormData(prev => ({ ...prev, matchId, dateTime }));
      }
    };

    updateAutoFields();
    const interval = setInterval(updateAutoFields, 1000);
    return () => clearInterval(interval);
  }, [setupData]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Effacer l'erreur du champ modifié
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.playerA.trim()) {
      newErrors.playerA = t('screens.setup.validation.playerARequired');;
    }

    if (!formData.playerB.trim()) {
      newErrors.playerB = t('screens.setup.validation.playerBRequired');
    }

    if (!formData.scoringType) {
      newErrors.scoringType = t('screens.setup.validation.scoringTypeRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build match data in the format expected by the app
      const matchData: Record<string, any> = {
        matchId: formData.matchId,
        competitionName: formData.competitionName.trim() || 'No competition',
        categoryName: formData.categoryName.trim() || 'Unknown category',
        drawName: formData.drawName.trim() || 'Match simple',
        refereeName: formData.refereeName.trim() || 'Unknown referee',
        dateTime: formData.dateTime,
        courtName: formData.courtName.trim() || undefined,
        scoringType: formData.scoringType,
        playerA: formData.playerA.trim(),
        playerB: formData.playerB.trim(),
        resultCallbackUrl: setupData ? setupData.resultCallbackUrl : config.defaultCallbackUrl
      };

      // Merge QR fields when coming from a QR scan
      if (setupData) {
        matchData.context = setupData.context;
        matchData.centerId = setupData.centerId;
        matchData.centerName = setupData.centerName;
        matchData.courtId = setupData.courtId;
        matchData.courtName = setupData.courtName;
        matchData.liveScoreUrl = setupData.liveScoreUrl;
      }

      if (formData.roundType) {
        matchData.roundType = formData.roundType;
      }

      // Save referee name preference for next matches
      localStorage.setItem(MANUAL_SETUP_STORAGE_KEY, JSON.stringify({
        refereeName: formData.refereeName.trim()
      }));

      // Clean up QR setup data before storing match data
      matchStorage.clearSetupInput();

      // Store data for the main app (session hand-off + persistent fallback)
      matchStorage.saveMatchInput(matchData);

      // Reload to trigger validation of new data
      window.location.reload();
      
    } catch (error) {
      //console.error('❌ Erreur lors de la création du match:', error);
      alert('Erreur lors de la création du match: ' + (error as Error).message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-4 bg-cover bg-center bg-no-repeat relative"
        style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/images/squash.webp')`,
            backgroundColor: '#dbeafe' // fallback (couleur bleu clair)
        }}
        >
        {/* Overlay semi-transparent pour la lisibilité */}
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Contenu avec z-index plus élevé */}
        <div className="relative z-10">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="p-6 mb-6" style={{ backgroundColor: '#F3F0EB' }}>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 text-center">
            {t('screens.setup.title')}
          </h1>
        </div>

        {/* QR context banner */}
        {setupData && (
          <div className="p-4 mb-6 bg-blue-50 border border-blue-200" data-testid="qr-context-banner">
            <div className="flex items-center gap-3">
              <QrCode className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-900">{t('screens.setup.qrContext.title')}</p>
                <p className="text-sm text-blue-700">
                  {setupData.centerName} - {setupData.courtName}
                </p>
                <p className="text-xs text-blue-600 mt-1">{t('screens.setup.qrContext.subtitle')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire */}
        <div className="p-6" style={{ backgroundColor: '#F3F0EB' }}>
          <form onSubmit={handleSubmit}>
            {/* Joueurs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  {t('screens.setup.fields.playerA')} *
                </label>
                <input
                  type="text"
                  value={formData.playerA}
                  onChange={(e) => handleInputChange('playerA', e.target.value)}
                  className={`w-full px-4 py-3 border-2 transition-colors ${
                    errors.playerA
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white'
                  }`}
                  placeholder={t('screens.setup.placeholders.playerName')}
                />
                {errors.playerA && (
                  <p className="mt-1 text-sm text-red-600">{errors.playerA}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  {t('screens.setup.fields.playerB')} *
                </label>
                <input
                  type="text"
                  value={formData.playerB}
                  onChange={(e) => handleInputChange('playerB', e.target.value)}
                  className={`w-full px-4 py-3 border-2 transition-colors ${
                    errors.playerB
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white'
                  }`}
                  placeholder={t('screens.setup.placeholders.playerName')}
                />
                {errors.playerB && (
                  <p className="mt-1 text-sm text-red-600">{errors.playerB}</p>
                )}
              </div>
            </div>

            {/* Type de scoring */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Trophy className="w-4 h-4 inline mr-2" />
                {t('screens.setup.fields.scoringType')} *
              </label>
              <select
                value={formData.scoringType}
                onChange={(e) => handleInputChange('scoringType', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              >
                <option value="BestOf3">{t('screens.setup.options.bestOf3')}</option>
                <option value="BestOf5">{t('screens.setup.options.bestOf5')}</option>
              </select>
            </div>

            {/* Section dépliable - Informations optionnelles */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowOptionalFields(!showOptionalFields)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors text-gray-700 font-semibold"
              >
                <span>{t('screens.setup.sections.optionalFields')}</span>
                {showOptionalFields ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {showOptionalFields && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200">
                  {/* Compétition et Catégorie */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('screens.setup.fields.competition')}
                      </label>
                      <input
                        type="text"
                        value={formData.competitionName}
                        onChange={(e) => handleInputChange('competitionName', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white focus:border-blue-500 transition-colors"
                        placeholder={t('screens.setup.placeholders.competition')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('screens.setup.fields.category')}
                      </label>
                      <input
                        type="text"
                        value={formData.categoryName}
                        onChange={(e) => handleInputChange('categoryName', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white focus:border-blue-500 transition-colors"
                        placeholder={t('screens.setup.placeholders.category')}
                      />
                    </div>
                  </div>

                  {/* Tableau et Court */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('screens.setup.fields.drawName')}
                      </label>
                      <input
                        type="text"
                        value={formData.drawName}
                        onChange={(e) => handleInputChange('drawName', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white focus:border-blue-500 transition-colors"
                        placeholder={t('screens.setup.placeholders.drawName')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('screens.setup.fields.courtName')}
                      </label>
                      <input
                        type="text"
                        value={formData.courtName}
                        onChange={(e) => handleInputChange('courtName', e.target.value)}
                        readOnly={!!setupData}
                        className={`w-full px-4 py-3 border-2 transition-colors ${
                          setupData
                            ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'border-gray-200 bg-white focus:border-blue-500'
                        }`}
                        placeholder={t('screens.setup.placeholders.courtName')}
                      />
                    </div>
                  </div>

                  {/* Type de tour et Arbitre */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('screens.setup.fields.roundType')}
                      </label>
                      <select
                        value={formData.roundType}
                        onChange={(e) => handleInputChange('roundType', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white focus:border-blue-500 transition-colors"
                      >
                        <option value="">{t('screens.setup.options.roundTypes.none')}</option>
                        <option value="pool">{t('screens.setup.options.roundTypes.pool')}</option>
                        <option value="r32">{t('screens.setup.options.roundTypes.r32')}</option>
                        <option value="r16">{t('screens.setup.options.roundTypes.r16')}</option>
                        <option value="qf">{t('screens.setup.options.roundTypes.qf')}</option>
                        <option value="sf">{t('screens.setup.options.roundTypes.sf')}</option>
                        <option value="final">{t('screens.setup.options.roundTypes.final')}</option>
                        <option value="third">{t('screens.setup.options.roundTypes.third')}</option>
                        <option value="friendly">{t('screens.setup.options.roundTypes.friendly')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('screens.setup.fields.referee')}
                      </label>
                      <input
                        type="text"
                        value={formData.refereeName}
                        onChange={(e) => handleInputChange('refereeName', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white focus:border-blue-500 transition-colors"
                        placeholder={t('screens.setup.placeholders.referee')}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bouton de soumission */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting || isConfigLoading}
                className={`inline-flex items-center gap-2 px-8 py-4 text-white font-semibold transition-all ${
                  isSubmitting || isConfigLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'hover:opacity-90 hover:-translate-y-0.5'
                }`}
                style={{ backgroundColor: isSubmitting ? undefined : '#4E4C4B' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin" />
                    {t('screens.setup.actions.creating')}
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    {t('screens.setup.actions.create')}
                  </>
                )}
              </button>
            </div>

          </form>

        </div>
      </div>
    </div>
    </div>
  );
}